import { FC, useSyncExternalStore } from 'react';

import { Box, Button, HStack, Text } from '@chakra-ui/react';

import type {
  ConnieVoiceCallStatus,
  MicLevelSource,
} from '@/hooks/useConnieVoiceCall';
import type { ConnieVoiceMode } from '@/services/connieVoice';

export type VoiceCallControl = {
  /** Who answers, in labels. Connie by default. */
  agentName?: string;
  status: ConnieVoiceCallStatus;
  mode: ConnieVoiceMode;
  /** Mic level, 0–1, read by the meter alone. */
  level: MicLevelSource;
  /** What the user is saying, before the turn ends. */
  caption: string;
  onStart: () => void;
  onStop: () => void;
  onEndTurn: () => void;
};

const statusLabel = (status: ConnieVoiceCallStatus, agentName: string) => {
  switch (status) {
    case 'connecting':
      return 'Connecting…';
    case 'listening':
    case 'hearing':
      return 'Listening';
    case 'thinking':
      return `${agentName} is answering`;
    default:
      return '';
  }
};

const METER_BARS = [0.55, 0.85, 1, 0.85, 0.55];

/** Subscribes to the mic level itself, so only the bars repaint as you talk. */
const MicMeter: FC<{ level: MicLevelSource; active: boolean }> = ({
  level,
  active,
}) => {
  const value = useSyncExternalStore(level.subscribe, level.get, () => 0);
  return (
    <HStack spacing='2px' h={4} flexShrink={0} aria-hidden>
      {METER_BARS.map((weight, index) => (
        <Box
          key={index}
          w='3px'
          borderRadius='full'
          bg={active ? 'whiteAlpha.700' : 'whiteAlpha.300'}
          h={`${Math.max(18, Math.round((active ? value : 0) * weight * 100))}%`}
          transition='height 90ms linear'
          sx={{
            '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
          }}
        />
      ))}
    </HStack>
  );
};

/** Calm in-input strip for a live call: state, mic level and live caption. */
export const VoiceCallBar: FC<{ call: VoiceCallControl }> = ({ call }) => {
  const listening = call.status === 'listening' || call.status === 'hearing';
  const showSend = call.mode === 'push_to_talk' && listening;
  // FSI ignores the mic while the agent answers, so say so rather than seem to listen.
  const hint =
    call.status === 'thinking'
      ? 'Wait for her to finish'
      : call.mode === 'push_to_talk'
        ? 'Tap send when you finish'
        : 'Pause to send';

  return (
    <HStack
      position='absolute'
      insetY={0}
      left={0}
      right={0}
      px={4}
      pr='136px'
      spacing={3}
      bg='gray.800'
      borderRadius='inherit'
      zIndex={2}
      data-cy='connie-voice-call'
      data-status={call.status}
    >
      <MicMeter level={call.level} active={listening} />
      <Text
        color='whiteAlpha.800'
        fontSize='sm'
        fontWeight='medium'
        flexShrink={0}
        aria-live='polite'
        data-cy='connie-voice-status'
      >
        {statusLabel(call.status, call.agentName ?? 'Connie')}
      </Text>
      <Text
        color='whiteAlpha.600'
        fontSize='sm'
        noOfLines={1}
        minW={0}
        data-cy='connie-voice-caption'
      >
        {call.caption || hint}
      </Text>
      {showSend && (
        <Button
          size='xs'
          variant='ghost'
          color='whiteAlpha.800'
          flexShrink={0}
          ml='auto'
          onClick={call.onEndTurn}
        >
          Send
        </Button>
      )}
    </HStack>
  );
};
